import { useState } from 'react'
import { toast } from 'sonner'
import { AP_INVOICE_PRIORITIES, AP_INVOICE_STATUSES } from '@/features/invoices/constants'
import { type ApPaymentPolicy, type ApInvoiceDetail } from '@/features/invoices/view-models'
import { type ApInvoiceUpdateInput } from '@/features/invoices/types'
import { useLinkInvoicePo } from '@/features/invoices/data/actions'
import type { useUpdateInvoice } from '@/features/invoices/mutations'
import { formatInvoicePriority, formatInvoiceStatus } from '@/features/invoices/status'
import { getApiErrorMessage } from '@/lib/api-error'

export function useInvoiceSummary(
  invoiceId: string,
  invoice: ApInvoiceDetail | undefined,
  updateInvoice: ReturnType<typeof useUpdateInvoice>,
) {
  const linkPo = useLinkInvoicePo(invoiceId)

  const [poNumber, setPoNumber] = useState('')
  const [poAllocationAmount, setPoAllocationAmount] = useState('')

  const [policyOpen, setPolicyOpen] = useState(false)
  const [policyDraft, setPolicyDraft] = useState<ApPaymentPolicy>('normal_terms')
  const [policyReason, setPolicyReason] = useState('')
  const [recurringInstallmentAmount, setRecurringInstallmentAmount] = useState('')
  const [recurringTotalCommitment, setRecurringTotalCommitment] = useState('')
  const [nextExpectedInvoiceDate, setNextExpectedInvoiceDate] = useState('')

  const savePatch = (label: string, patch: ApInvoiceUpdateInput, done: () => void) => {
    updateInvoice.mutate(patch, {
      onSuccess: () => {
        toast.success(`${label} saved`)
        done()
      },
      onError: (err) =>
        toast.error(getApiErrorMessage(err, 'Could not save this field. Try again.')),
    })
  }

  // These editors are rendered only after the page has an invoice, without remounting their state.
  const openPolicyEditor = () => {
    setPolicyDraft(invoice!.payment_policy || 'normal_terms')
    setPolicyReason(invoice!.payment_policy_reason || '')
    setRecurringInstallmentAmount(String(invoice!.recurring_installment_amount ?? ''))
    setRecurringTotalCommitment(String(invoice!.recurring_total_commitment ?? ''))
    setNextExpectedInvoiceDate(invoice!.next_expected_invoice_date || '')
    setPolicyOpen(true)
  }

  const savePaymentPolicy = () => {
    if (policyDraft !== 'normal_terms' && !policyReason.trim()) {
      toast.error('Explain why this payment policy applies')
      return
    }
    if (policyDraft === 'recurring_installment') {
      const amount = Number(recurringInstallmentAmount)
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error('Recurring installments require a positive installment amount')
        return
      }
      if (recurringTotalCommitment) {
        const commitment = Number(recurringTotalCommitment)
        if (!Number.isFinite(commitment) || commitment <= 0 || commitment < amount) {
          toast.error('Total commitment must be at least the installment amount')
          return
        }
      }
    }
    savePatch(
      'Payment policy',
      {
        payment_policy: policyDraft,
        payment_policy_reason: policyReason.trim() || null,
        payment_policy_override: policyDraft !== 'normal_terms',
        recurring_installment_amount:
          policyDraft === 'recurring_installment' ? recurringInstallmentAmount : null,
        recurring_total_commitment:
          policyDraft === 'recurring_installment' ? recurringTotalCommitment || null : null,
        next_expected_invoice_date:
          policyDraft === 'recurring_installment' ? nextExpectedInvoiceDate || null : null,
        hold_reason: policyDraft === 'project_hold' ? policyReason.trim() : null,
      },
      () => setPolicyOpen(false),
    )
  }

  const submitPoLink = (close?: () => void) => {
    const trimmed = poNumber.trim()
    if (!trimmed) {
      toast.error('PO number is required')
      return
    }
    const allocation = poAllocationAmount.trim()
    if (allocation && (!Number.isFinite(Number(allocation)) || Number(allocation) <= 0)) {
      toast.error('PO allocation must be greater than zero')
      return
    }
    linkPo.mutate(
      { po_number_raw: trimmed, allocation_amount: allocation || null },
      {
        onSuccess: () => {
          toast.success('PO linked')
          setPoNumber('')
          setPoAllocationAmount('')
          close?.()
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'PO link failed')),
      },
    )
  }

  const statusOptions = AP_INVOICE_STATUSES.map((status) => ({
    value: status,
    label: formatInvoiceStatus(status),
  }))
  const priorityOptions = AP_INVOICE_PRIORITIES.map((priority) => ({
    value: priority,
    label: formatInvoicePriority(priority),
  }))
  return {
    linkPo,
    poNumber,
    setPoNumber,
    poAllocationAmount,
    setPoAllocationAmount,
    policyOpen,
    setPolicyOpen,
    policyDraft,
    setPolicyDraft,
    policyReason,
    setPolicyReason,
    recurringInstallmentAmount,
    setRecurringInstallmentAmount,
    recurringTotalCommitment,
    setRecurringTotalCommitment,
    nextExpectedInvoiceDate,
    setNextExpectedInvoiceDate,
    savePatch,
    openPolicyEditor,
    savePaymentPolicy,
    submitPoLink,
    statusOptions,
    priorityOptions,
  }
}
